import { useState, useCallback, useRef, useEffect } from 'react';
import { message } from 'antd';
import axios from 'axios';

/**
 * 品牌网络数据管理Hook
 * 负责品牌竞争网络的加载、生成和详情数据获取
 */
export const useBrandNetwork = (skuId: string | undefined) => {
  const [networkData, setNetworkData] = useState<any>(null);
  const [brandDetails, setBrandDetails] = useState<any[]>([]);
  const [networkLoading, setNetworkLoading] = useState(false);
  const [brandDetailsLoading, setBrandDetailsLoading] = useState(false);
  const [autoGenerateAttempted, setAutoGenerateAttempted] = useState(false);
  const fetchNetworkDataRef = useRef<(autoGenerate?: boolean) => Promise<void>>();

  const fetchBrandDetails = useCallback(async () => {
    if (!skuId) return;

    try {
      setBrandDetailsLoading(true);
      const response = await axios.get(`http://localhost:8000/api/brand-network/brand_details/`, {
        params: { sku_id: skuId }
      });
      const brands = response.data.brands;
      setBrandDetails(Array.isArray(brands) ? brands : []);
    } catch (error) {
      console.error('加载品牌详细数据失败:', error);
    } finally {
      setBrandDetailsLoading(false);
    }
  }, [skuId]);

  const generateBrandNetwork = useCallback(async (isManual = false) => {
    if (!skuId) return;

    try {
      message.loading({ content: '正在生成品牌竞争数据...', key: 'generate', duration: 0 });
      const response = await axios.post('/api/brand-network/generate/', {
        sku_id: skuId,
      });

      console.log('生成品牌网络响应:', response.data);

      // 检查是否同步执行完成
      if (response.status === 200) {
        message.success({ content: '品牌竞争数据生成成功！', key: 'generate' });
      } else {
        message.success({ content: '品牌竞争数据生成任务已提交，请稍后刷新', key: 'generate' });
      }

      // 如果是手动触发，重置自动生成标志
      if (isManual) {
        setAutoGenerateAttempted(false);
      }

      // 延迟一点再重新加载数据，确保数据已写入数据库
      setTimeout(() => {
        fetchNetworkDataRef.current?.(false); // 不再自动触发生成，避免循环
      }, 1000);
    } catch (error: any) {
      console.error('生成品牌竞争数据失败:', error);
      console.error('错误详情:', error.response?.data);
      const errorMsg = error.response?.data?.error || error.message || '生成品牌竞争数据失败';
      const errorDetails = error.response?.data?.details;

      if (errorDetails) {
        console.error('错误堆栈:', errorDetails);
      }

      message.error({ content: errorMsg, key: 'generate', duration: 5 });
    }
  }, [skuId]);

  const fetchNetworkData = useCallback(async (autoGenerate = true) => {
    if (!skuId) return;

    try {
      setNetworkLoading(true);
      const response = await axios.get('/api/brand-network/for_sku/', {
        params: {
          sku_id: skuId,
          min_frequency: 1,
        }
      });
      console.log('品牌网络数据:', response.data);
      setNetworkData(response.data);

      // 同时加载品牌详细数据
      await fetchBrandDetails();

      // 如果没有数据、允许自动生成、且未尝试过自动生成，则自动触发生成
      if (autoGenerate &&
        !autoGenerateAttempted &&
        (!response.data.nodes || response.data.nodes.length === 0)) {
        console.log('检测到无品牌数据，自动触发生成...');
        message.info({ content: '检测到无品牌数据，正在自动生成...', key: 'auto-generate', duration: 2 });
        setAutoGenerateAttempted(true);
        // 延迟一点再调用，避免阻塞UI
        setTimeout(() => {
          generateBrandNetwork();
        }, 500);
      }
    } catch (error) {
      console.error('获取品牌网络数据失败:', error);
      message.error('获取品牌网络数据失败');
    } finally {
      setNetworkLoading(false);
    }
  }, [skuId, autoGenerateAttempted, fetchBrandDetails, generateBrandNetwork]);

  useEffect(() => {
    fetchNetworkDataRef.current = fetchNetworkData;
  }, [fetchNetworkData]);

  return {
    networkData,
    brandDetails,
    networkLoading,
    brandDetailsLoading,
    fetchNetworkData,
    generateBrandNetwork,
    fetchBrandDetails,
  };
};

